import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import type { Document, SignatureRequest } from '../../../js/api';
import { DocumentsService, SignaturesService } from '../../../js/api';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import { Dialog, DialogBody, DialogDescription, DialogTitle } from './dialog';
import { SignatureSignBody } from './signature-sign-body';

type DocumentSignDialogProps = {
  open: boolean;
  /** The document being signed; must carry an embedded `signature`. */
  doc: Document | null;
  onClose: () => void;
  /** Receives the updated request so the caller can react (e.g. prompt to invite
   *  the next signer). */
  onSuccess: (signatureRequest: SignatureRequest) => void;
};

/**
 * In-app (session-authenticated) signing dialog. Opens the document inline,
 * logs a VIEW on open, and submits the signature via the signatures API.
 */
export function DocumentSignDialog({ open, doc, onClose, onSuccess }: DocumentSignDialogProps) {
  const signatureRequestId = doc?.signature?.id ?? null;
  const alreadyViewedRef = useRef<string | null>(null);

  // Presigned inline URL for the PDF preview.
  const { data: download } = useQuery({
    queryKey: ['document-inline', doc?.id],
    queryFn: () =>
      DocumentsService.documentsDownloadRetrieve({ id: doc!.id, disposition: 'inline' }),
    enabled: open && !!doc?.id,
  });

  // Log a VIEW exactly once per opened request (audit trail).
  useEffect(() => {
    if (!open || !signatureRequestId) return;
    if (alreadyViewedRef.current === signatureRequestId) return;
    alreadyViewedRef.current = signatureRequestId;
    SignaturesService.signaturesRequestsViewCreate({ id: signatureRequestId }).catch(() => {
      // A failed view-log must never block signing; it's best-effort audit.
    });
  }, [open, signatureRequestId]);

  useEffect(() => {
    if (!open) alreadyViewedRef.current = null;
  }, [open]);

  const signMutation = useMutation({
    mutationFn: (signedName: string) =>
      SignaturesService.signaturesRequestsSignCreate({
        id: signatureRequestId!,
        requestBody: { signed_name: signedName, consent: true },
      }),
    onSuccess: (data) => {
      toast.success('Document signed');
      onSuccess(data);
      onClose();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to sign document', handle403: true });
    },
  });

  const alreadySigned = doc?.signature ? !doc.signature.viewer_can_sign : false;

  return (
    <Dialog open={open} onClose={() => !signMutation.isPending && onClose()} size="3xl">
      <DialogTitle>Sign {doc?.label || doc?.original_filename || 'document'}</DialogTitle>
      <DialogDescription>
        Review the document below, then type your name to sign electronically.
      </DialogDescription>
      <DialogBody>
        {signatureRequestId ? (
          <SignatureSignBody
            documentUrl={download?.url ?? null}
            documentLabel={doc?.label || doc?.original_filename || 'document'}
            isSubmitting={signMutation.isPending}
            onSign={(signedName) => signMutation.mutate(signedName)}
            onCancel={() => onClose()}
            alreadySigned={alreadySigned}
          />
        ) : null}
      </DialogBody>
    </Dialog>
  );
}
